import { StyleSheet } from 'react-native'
import type { ViewStyle } from 'react-native'
import type { Theme } from '../../src/theme/getTheme'
import type { TargetRect } from '../../src/create/types/guestWalkthrough.types'

const WALKTHROUGH_DIM_COLOR = 'rgba(0, 0, 0, 0.56)'
const SPOTLIGHT_PROXY_EXPAND_PX = 10

export function getWalkthroughOverlayStyles(theme: Theme) {
  return StyleSheet.create({
    /** Fills the Modal — no zIndex needed; Modal already renders above everything */
    overlay: {
      flex: 1,
    },
    /** Large-border "spotlight" view. Positioned so the inner content area (hole) sits over the target.
     *  borderColor is the dim; the hole is the transparent inner box. */
    spotlightBorder: {
      position: 'absolute',
      borderColor: WALKTHROUGH_DIM_COLOR,
    },
    /** Full-screen dim while targetRect is null (loading / no spotlight) */
    fullDim: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: WALKTHROUGH_DIM_COLOR,
    },
    /** Tooltip card — position absolute; horizontal/vertical position set dynamically.
     *  Subtle primary-tinted border so the card reads as an overlay distinct from the app underneath. */
    tooltipCard: {
      position: 'absolute',
      backgroundColor: theme.background.card,
      borderRadius: 18,
      padding: 16,
      borderWidth: 1,
      borderColor: theme.primaryLight + '55',
      shadowColor: theme.shadow,
      shadowOffset: { width: 0, height: 6 },
      shadowOpacity: 0.22,
      shadowRadius: 16,
      elevation: 10,
    },
    /** Top row: monkey avatar | text column */
    mainRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 12,
      marginBottom: 14,
    },
    monkeyImage: {
      width: 80,
      height: 80,
      borderRadius: 40,
      flexShrink: 0,
    },
    textColumn: {
      flex: 1,
    },
    /** Step counter + skip in same row at the top of the text column */
    stepRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 6,
    },
    stepLabel: {
      fontSize: 12,
      fontWeight: '600' as const,
      color: theme.gray[400],
      letterSpacing: 0.2,
    },
    tooltipText: {
      fontSize: 15,
      lineHeight: 22,
      color: theme.black,
      fontWeight: '500' as const,
    },
    /** Progress dots */
    progressRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      marginBottom: 12,
    },
    progressDot: {
      width: 6,
      height: 6,
      borderRadius: 3,
      backgroundColor: 'rgba(0,0,0,0.15)',
    },
    progressDotActive: {
      width: 18,
      backgroundColor: theme.primary,
    },
    nextButton: {
      backgroundColor: theme.primary,
      borderRadius: 12,
      paddingVertical: 13,
      alignItems: 'center',
      justifyContent: 'center',
    },
    nextButtonText: {
      color: theme.white,
      fontWeight: '700' as const,
      fontSize: 15,
    },
    skipButton: {
      paddingHorizontal: 6,
      paddingVertical: 2,
    },
    skipButtonText: {
      color: theme.gray[500],
      fontSize: 13,
      fontWeight: '500' as const,
    },
    /** Transparent hit-area inside the Modal that proxies taps on spotlighted elements */
    spotlightProxy: {
      position: 'absolute',
    },
    /** Hand_tap Lottie — positioned absolutely over the spotlight center */
    handTapContainer: {
      position: 'absolute',
      width: 96,
      height: 96,
      backgroundColor: 'transparent',
      overflow: 'hidden',
    },
    handTapLottie: {
      width: 96,
      height: 96,
      backgroundColor: 'transparent',
    },
  })
}

/** Returns the absolute-position style for the large-border spotlight hole. */
export function getSpotlightBorderPositionStyle(
  targetRect: TargetRect,
  borderWidth: number,
  borderRadius: number,
): ViewStyle {
  return {
    left: targetRect.x - borderWidth,
    top: targetRect.y - borderWidth,
    width: targetRect.width + borderWidth * 2,
    height: targetRect.height + borderWidth * 2,
    borderWidth,
    borderRadius: borderRadius + borderWidth,
  }
}

/** Returns the absolute-position style for a proxy Pressable over the spotlighted element. */
export function getSpotlightProxyPositionStyle(targetRect: TargetRect): ViewStyle {
  return {
    left: targetRect.x - SPOTLIGHT_PROXY_EXPAND_PX,
    top: targetRect.y - SPOTLIGHT_PROXY_EXPAND_PX,
    width: targetRect.width + SPOTLIGHT_PROXY_EXPAND_PX * 2,
    height: targetRect.height + SPOTLIGHT_PROXY_EXPAND_PX * 2,
  }
}

/** Returns the absolute-position style for the hand-tap Lottie centred over the spotlight. */
export function getHandTapPositionStyle(
  targetRect: TargetRect,
  halfSize: number,
  offsetX: number,
  offsetY: number,
): ViewStyle {
  return {
    left: targetRect.x + targetRect.width / 2 - halfSize + offsetX,
    top: targetRect.y + targetRect.height / 2 - halfSize + offsetY,
  }
}

const TOOLTIP_MIN_SPACE_BELOW = 150
const TOOLTIP_BELOW_OFFSET = 16
const TOOLTIP_ABOVE_OFFSET = 16
const TOOLTIP_DEFAULT_TOP_RATIO = 0.45

/** Returns the top/bottom position style for the tooltip card relative to the spotlighted element. */
export function getTooltipPositionStyle(
  targetRect: TargetRect | null,
  screenHeight: number,
): ViewStyle {
  if (targetRect === null) {
    return { top: Math.round(screenHeight * TOOLTIP_DEFAULT_TOP_RATIO) }
  }
  const spotBottom = targetRect.y + targetRect.height
  const spaceBelow = screenHeight - spotBottom
  if (spaceBelow >= TOOLTIP_MIN_SPACE_BELOW) {
    return { top: spotBottom + TOOLTIP_BELOW_OFFSET }
  }
  return { bottom: screenHeight - targetRect.y + TOOLTIP_ABOVE_OFFSET }
}
